import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IConversationViewModel, IFileViewModel, IMessageViewModel } from 'viewModels';
import { IConversationInputModel } from 'inputModels';
import ConversationController from '../controller/ConversationController';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './auth';
import { MessageType, TypeUser } from '../types/enums';

type ConversationContextProps = {
  children: ReactNode;
};

interface IInitialValues {
  conversations: IConversationViewModel[];
  setConversations: React.Dispatch<React.SetStateAction<IConversationViewModel[]>>;
  activeConversationId: string | undefined;
  setActiveConversationId: React.Dispatch<React.SetStateAction<string | undefined>>;
  getConversationIdByParticipantsIds: (participantsIds: string[]) => Promise<string>;
  refetchConversations: () => void;
}

const ConversationContext = React.createContext({} as IInitialValues);

const ConversationContextProvider = ({ children }: ConversationContextProps) => {
  const { user, tokenIsValid } = useAuth();
  const [conversations, setConversations] = useState<IConversationViewModel[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>();

  const getConversationIdByParticipantsIds = useCallback(
    async (participantsIds: string[]) => {
      const conversationId = conversations?.find((c) => {
        const ids = c.participants?.map((p) => p.id).sort();
        const sortedParticipants = [...participantsIds].sort();

        if (ids.length !== sortedParticipants?.length) return false;

        return ids.every((id, index) => id === sortedParticipants[index]);
      })?.id;

      if (!conversationId) {
        const conversationInput: IConversationInputModel = {
          participants: participantsIds,
          message: [],
        };
        const newConversationId = await ConversationController.createConversation(
          conversationInput,
        );
        return newConversationId;
      }
      return conversationId;
    },
    [conversations],
  );
  useEffect(() => {
    if (!tokenIsValid) {
      setConversations([]);
      setActiveConversationId(undefined);
    }
  }, [tokenIsValid]);
  const refetchConversations = useCallback(() => {
    if (tokenIsValid && user.id) {
      ConversationController.getAll(
        user.type === TypeUser.COLLABORATOR ? user.companyId ?? '' : user.id,
      ).then((response) => {
        setConversations([
          ...response.map((cv) => {
            return {
              ...cv,
              messages: cv.messages.sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
              ),
            };
          }),
        ]);
      });
    }
  }, [tokenIsValid, user.companyId, user.id, user.type]);
  useEffect(() => {
    if (tokenIsValid && user.id) {
      ConversationController.getAll(
        user.type === TypeUser.COLLABORATOR ? user.companyId ?? '' : user.id,
      ).then((response) => {
        setConversations([
          ...response.map((cv) => {
            return {
              ...cv,
              messages: cv.messages.sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
              ),
            };
          }),
        ]);
      });
    }
  }, [user.companyId, user.id, user.type, tokenIsValid]);

  useEffect(() => {
    if (activeConversationId && !conversations.some((c) => c.id === activeConversationId)) {
      ConversationController.get(activeConversationId).then((response) => {
        setConversations((prev) => [
          ...prev,
          {
            ...response,
            messages: response.messages?.sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            ),
          },
        ]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId]);

  useWebSocket<IMessageViewModel>({
    url: `conversation/${user.id}/send-message`,
    onMessage: (message) => {
      const newConversations = conversations;
      const idx = conversations.findIndex((item) => item.id === message.conversationId);
      if (idx !== -1) {
        newConversations[idx].messages.push(message);
      }
      setConversations([...newConversations]);
    },
  });

  useWebSocket<{ audioFile: IFileViewModel; userId: string }>({
    url: `conversation/${activeConversationId}/send-audio`,
    onMessage: ({ audioFile, userId }) => {
      const newConversations = conversations;
      const idx = conversations.findIndex((item) => item.id === activeConversationId);
      if (idx !== -1) {
        newConversations[idx].messages.push({
          attachment: audioFile,
          conversationId: activeConversationId as string,
          createdById: userId,
          createdAt: new Date(),
          text: '',
          messageType: MessageType.AUDIO,
        });
      }
      setConversations([...newConversations]);
    },
  });

  useWebSocket<IConversationViewModel[]>({
    url: `conversation/${user.id}`,
    onMessage: (cvs) => {
      setConversations([
        ...cvs.map((cv) => {
          return {
            ...cv,
            messages: cv.messages.sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            ),
          };
        }),
      ]);
    },
  });

  useWebSocket<{ readMessageIds: string[]; userId: string }>({
    url: `conversation/${activeConversationId}/read-messages`,
    onMessage: (message) => {
      setConversations((prev) => {
        const newConversations = [...prev];
        const idx = prev.findIndex((item) => item.id === activeConversationId);
        if (idx !== -1) {
          newConversations[idx].messages.map((m) => {
            const info = m.infos?.find((i) => i.createdById === message.userId);
            if (info) {
              info.isReaded = true;
              info.readedAt = new Date();
            }
            return m;
          });
        }
        return newConversations;
      });
    },
  });
  const contextValues = useMemo(
    () => ({
      conversations,
      setConversations,
      activeConversationId,
      setActiveConversationId,
      getConversationIdByParticipantsIds,
      refetchConversations,
    }),
    [conversations, activeConversationId, getConversationIdByParticipantsIds, refetchConversations],
  );
  return (
    <ConversationContext.Provider value={contextValues}>{children}</ConversationContext.Provider>
  );
};
export const useConversation = () => useContext(ConversationContext);
export default ConversationContextProvider;
